import _ from 'underscore';

import { Helpers } from '~commonInfra/utils/Helpers';
import { IGroupSetInfo } from '~src/userManagement/groupsets/models/IGroupSetInfo';

import { GroupKeys, IGroup } from '../../userManagement/groups/serverApi/types/IGroup';
import { IUserName, UserKeys } from '../../userManagement/users/serverApi/types/IUser';
import { IGroupSetWebClientModel, IUserPermissionable } from '../requests/types/GetExplicitPermissionsRequest';
import { IAuthorizableGranteePermissions } from '../requests/types/IAuthorizableGranteePermissions';
import { CapabilityValue, ICapabilityValue } from '../requests/types/ICapabilityValue';
import { IGranteePermissions } from '../types/IGranteePermissions';
import { IPermission, Permission, PermissionReason } from '../types/Permission';

export module PermissionsMerger {

  /**
   * Converts a list of IAuthorizableGranteePermissions from the server into an equivalent list of
   * IGranteePermissions for use by the client. The input array can contain AGPs for multiple
   * authorizables and multiple users, but their client-side authorizable type must be the same.
   */
  export function makeGranteePermissionsList(
    agps: IAuthorizableGranteePermissions[],
    authorizableIds: string[],
    groups: IGroup[],
    users: IUserPermissionable[],
    groupSets: IGroupSetWebClientModel[],
    usersCanBeProjectLeader?: IUserName[]
  ): IGranteePermissions[] {
    const groupDict = Helpers.dictFromArray<IGroup>(groups, GroupKeys.id);
    const userDict = Helpers.dictFromArray<IUserPermissionable>(users, UserKeys.id);
    const groupSetDict = Helpers.dictFromArray<IGroupSetWebClientModel>(groupSets, 'id'); // todo: create a groupsetkeys object
    let usersCanBeProjectLeaderDict: _.Dictionary<IUserName>;
    if (usersCanBeProjectLeader) {
      usersCanBeProjectLeaderDict = Helpers.dictFromArray<IUserName>(usersCanBeProjectLeader, UserKeys.id);
    }

    // Remove create_refresh_metrics capability from all AGPs 
    // see @W-17590382, @W-18521289 
    // This is a temporary solution to remediate a customer bug
    // The long term solution is to remove the capability from the server

    // Step 1: Filter out the problematic capability from each AGP's permissions array
    // This mutates the existing AGP objects but doesn't change the agps array structure
    _(agps).each((agp: IAuthorizableGranteePermissions) => {
      agp.permissions = _.filter(agp.permissions, (permission: ICapabilityValue) => permission.capability !== 'create_refresh_metrics');
    });

    // Step 2: Remove AGPs that have no permissions left after filtering
    // This creates a new array containing only AGPs with at least one permission
    // Using _.filter instead of splice() avoids the "modifying array during iteration" bug
    agps = _.filter(agps, (agp: IAuthorizableGranteePermissions) => !_.isEmpty(agp.permissions));

    const agpListByGrantee = _.groupBy(agps,
      (agp: IAuthorizableGranteePermissions) => agp.grantee.type + agp.grantee.id);
    let permissionsList: IGranteePermissions[] = _.map(agpListByGrantee,
      (granteeAgpList: IAuthorizableGranteePermissions[]) => {
        return createGranteePermissions(
          granteeAgpList,
          authorizableIds,
          groupDict,
          userDict,
          groupSetDict,
          usersCanBeProjectLeaderDict);
      });

    // system user should not show up in permissions grid
    const systemUser = _.findWhere(users, { username: '_system' });
    if (systemUser) {
      const systemUserGP = _.find(permissionsList, (gp: IGranteePermissions) => (gp.user && gp.user.id === systemUser.id));
      permissionsList = _.without(permissionsList, systemUserGP);
    }

    return permissionsList;
  }

  function createGranteePermissions(
    granteeAgpList: IAuthorizableGranteePermissions[],
    authorizableIds: string[],
    groupDict: _.Dictionary<IGroup>,
    userDict: _.Dictionary<IUserPermissionable>,
    groupSetDict: _.Dictionary<IGroupSetWebClientModel>,
    usersCanBeProjectLeaderDict?: _.Dictionary<IUserName>
  ): IGranteePermissions {
    // we know all grantees in this list are the same because of the groupBy
    const grantee = granteeAgpList[0].grantee;

    const granteePermissions: IGranteePermissions = {
      permissionsByContentType: mergePermissionListByContentType(granteeAgpList, authorizableIds)
    };

    switch (grantee.type) {
      case 'group':
        granteePermissions.group = groupDict[grantee.id];
        break;
      case 'user':
        granteePermissions.user = userDict[grantee.id];
        if (usersCanBeProjectLeaderDict && usersCanBeProjectLeaderDict[grantee.id] === undefined) {
          granteePermissions.user.disallowProjectLeader = true;
        }
        break;
      case 'groupAND':
        const groupSetInfo: IGroupSetInfo = {
          contentType: 'groupset',
          id: groupSetDict[grantee.id].groupSetLuid,
          localId: parseInt(groupSetDict[grantee.id].id),
          name: groupSetDict[grantee.id].name,
          groupCount: groupSetDict[grantee.id].groupCount,
        };
        granteePermissions.groupSet = groupSetInfo;
        break;
    }
    return granteePermissions;
  }

  /**
   * Merges a list of IAuthorizableGranteePermissions to a single dictionary that maps from
   * capability to permission. Ignores the grantee (assumes all the items in the input list are
   * for a single grantee).
   */
  function mergePermissionListByContentType(
    granteeAgpList: IAuthorizableGranteePermissions[],
    authorizableIds: string[]
  ): _.Dictionary<_.Dictionary<IPermission>> {
    // The permissions for each authorizable type will end up as a separate section
    // in the IGranteePermissions, so we process each individually.
    const agpsByContentType = _.groupBy(granteeAgpList,
      (agp: IAuthorizableGranteePermissions) => agp.authorizable.type);

    const permissionsByContentType: _.Dictionary<_.Dictionary<IPermission>> = {};

    _.each(agpsByContentType,
      (agps: IAuthorizableGranteePermissions[], contentType: string) => {
        const permissions = aggregatePermissionsForAuthorizables(agps, authorizableIds);
        permissionsByContentType[contentType] = permissions;
      }
    );

    return permissionsByContentType;
  }

  function aggregatePermissionsForAuthorizables(
    agpsForAuthorizable: IAuthorizableGranteePermissions[],
    authorizableIds: string[]
  ): _.Dictionary<IPermission> {
    // There may not be an agp for every authorizable we care about, so we have to merge the
    // permissions by authorizable ID, rather than by agp.
    const agpByAuthorizable = _.indexBy(agpsForAuthorizable,
      (agp: IAuthorizableGranteePermissions) => agp.authorizable.id);

    let aggregatedPermissions: _.Dictionary<IPermission> = null;

    authorizableIds.forEach((authorizableId: string) => {
      const agp = agpByAuthorizable[authorizableId];
      const inputPermissions: _.Dictionary<IPermission> =
        agp ? permissionsFromCapabilityValues(agp.permissions) : {};
      if (aggregatedPermissions) {
        mergePermissions(aggregatedPermissions, inputPermissions);
      } else {
        aggregatedPermissions = inputPermissions;
      }
    });

    return aggregatedPermissions;
  }

  function permissionsFromCapabilityValues(
    capabilityValues: ICapabilityValue[]
  ): _.Dictionary<IPermission> {
    const permissions: _.Dictionary<IPermission> = {};
    _.each(capabilityValues, (capabilityValue: ICapabilityValue) => {
      const capability = capabilityValue.capability;
      const permission = permissionFromCapabilityValue(capabilityValue);
      const reason = reasonFromCapabilityValue(capabilityValue);
      if (permissions[capability]) {
        permissions[capability].permission = permission;
        permissions[capability].reason = reason;
      } else {
        permissions[capability] = { permission: permission, reason: reason };
      }
    });
    return permissions;
  }

  function permissionFromCapabilityValue(capability: ICapabilityValue): Permission {
    const capabilityValue = capability.value;
    switch (capabilityValue) {
      case CapabilityValue.Allowed: return Permission.Allowed;
      case CapabilityValue.Denied: return Permission.Denied;
      // A single capability value from the server is never "mixed".
      default: return Permission.Unspecified;
    }
  }

  function reasonFromCapabilityValue(capability: ICapabilityValue): PermissionReason {
    const capabilityReason = capability.reason;
    switch (capabilityReason) {
      case 'GROUP_ALLOW': return PermissionReason.GroupAllow;
      case 'GROUP_DISALLOW': return PermissionReason.GroupDeny;
      case 'GROUP_PROJECT_LEADER': return PermissionReason.GroupProjectLeaderAllow;
      case 'GUEST_ALLOWED': return PermissionReason.GuestAllow;
      case 'DERIVED_ALLOW': return PermissionReason.DerivedAllow;
      case 'DERIVED_WRITE_ALLOW': return PermissionReason.DerivedWriteAllow;
      case 'IMPLICIT_DENY': return PermissionReason.ImplicitDeny;
      case 'USER_ADMIN': return PermissionReason.UserAdminAllow;
      case 'USER_ALLOW': return PermissionReason.UserAllow;
      case 'USER_AUTHORIZABLE_OWNER': return PermissionReason.UserAuthorizableOwnerAllow;
      case 'USER_DISALLOW': return PermissionReason.UserDeny;
      case 'USER_LICENSING': return PermissionReason.UserLicensingDeny;
      case 'USER_PROJECT_LEADER': return PermissionReason.UserProjectLeaderAllow;
      case 'USER_PROJECT_OWNER': return PermissionReason.UserProjectOwnerAllow;
      case 'GROUP_AND_ALLOW': return PermissionReason.GroupAndAllow;
      case 'GROUP_AND_DISALLOW': return PermissionReason.GroupAndDisallow;
      default: return PermissionReason.Unspecified;
    }
  }

  function mergePermissions(
    current: _.Dictionary<IPermission>,
    input: _.Dictionary<IPermission>
  ): void {
    _.each(_.union(_.keys(current), _.keys(input)), (capability: string) => {

      // some vars just to improve readability
      const curr = current[capability];
      const inpt = input[capability];

      if (curr && inpt) {
        if (curr.permission !== inpt.permission) {
          // the values explicitly differ
          current[capability].permission = Permission.Mixed;
          current[capability].reason = PermissionReason.Mixed;
        } else {
          // the values are explicitly equal
          if (curr.reason !== inpt.reason) {
            // the reasons explicitly differ
            current[capability].reason = PermissionReason.Mixed;
          }
        }

      } else if (curr) { // && !inpt
        if (curr.permission !== Permission.Unspecified) {
          // input is implicitly unspecified, current is explicitly specified
          current[capability].permission = Permission.Mixed;
          current[capability].reason = PermissionReason.Mixed;
        }

      } else if (inpt) { // && !curr
        if (inpt.permission !== Permission.Unspecified) {
          // input is explicitly specified, current is implicitly unspecified
          current[capability] = { permission: Permission.Mixed, reason: PermissionReason.Mixed };
        } else {
          // input is explicitly unspecified, current is implicitly unspecified
          current[capability] = { permission: inpt.permission, reason: inpt.reason };
        }
      }

    });
  }
}
